import { Component, ElementRef, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CommonModule, CurrencyPipe} from '@angular/common';
import { Experience } from '../services/interfaces/experiences';

@Component({
  selector: 'app-extra-dropdown',
  standalone: true,
  imports: [ButtonModule, CardModule, CommonModule,CurrencyPipe],
  templateUrl: './extra-dropdown.component.html',
  styleUrl: './extra-dropdown.component.scss'
})
export class ExtraDropdownComponent implements OnInit, OnChanges {
  @Input() experience: Experience | undefined;
  @Input() isLanguageDateAndTimeSelected: boolean | undefined ;
  @Input() isExtraDisabled: boolean = false;
  @Output() isValidChange = new EventEmitter<boolean>();
  @Output() totalPriceChange = new EventEmitter<number>();
  dropdownOpen: boolean = false;
  numberOfPeople: number = 0;
  extraLabelCounts: number[] = [];
  isValid: boolean = false;
  totalPeople: any;
  dropdownLabel: string = 'Extra';  
  maxLabelLength: number = 20;
  totalPrice: number = 0;
  private mappedExtras: any[] = []; // Cache degli extra mappati
  constructor(
    private cp: CurrencyPipe,
    private eRef: ElementRef
  ) { 

    
  }

  // Getter per accedere agli extra dalla nuova struttura (experience.prices con priceType === "EXTRA")
  get experiencePriceExtras(): any[] {
    if (!this.experience?.prices) {
      this.mappedExtras = [];
      return [];
    }
    
    // Filtra gli extra dalla nuova struttura
    const extrasFromPrices = this.experience.prices
      .filter(p => p.priceType === 'EXTRA')
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    
    // Se gli extra sono cambiati o non sono ancora mappati, ricrea il mapping
    if (this.mappedExtras.length !== extrasFromPrices.length || 
        this.mappedExtras.length === 0 ||
        this.mappedExtras.some((mapped, idx) => mapped.id !== extrasFromPrices[idx]?.id)) {
      this.mappedExtras = extrasFromPrices.map(price => ({
        // Mappa la nuova struttura alla vecchia struttura che si aspetta il componente
        id: price.id,
        name: price.priceTitle || '',
        price_cents: price.price || 0,
        priceCurrency: price.priceCurrency || 'EUR',
        currentQuantity: (price as any).currentQuantity || 0, // Mantieni se già presente, altrimenti 0
        minQuantity: (price as any).minQuantity || 0,
        maxQuantity: (price as any).maxQuantity || Infinity,
        position: price.position || 0,
        extraMetadata: price.extraMetadata || {}
      }));
    }
    
    return this.mappedExtras;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Se l'experience cambia, resetta la cache e reinizializza
    if (changes['experience'] && !changes['experience'].firstChange) {
      this.mappedExtras = [];
      const extras = this.experiencePriceExtras;
      this.extraLabelCounts = extras.map(() => 0);
      extras.forEach(extra => extra.currentQuantity = 0);
      this.calculateTotalPrice();
      this.updateValidation();
    }
  }

  ngOnInit() {
    // Inizializza i contatori degli extra dalla nuova struttura
    const extras = this.experiencePriceExtras;
    this.extraLabelCounts = extras.map(() => 0);
    // Inizializza anche currentQuantity negli oggetti mappati
    extras.forEach(extra => extra.currentQuantity = 0);
    this.calculateTotalPrice(); // Aggiorna il prezzo dopo l'incremento degli extra
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  formatCurrencyCents(number: number | undefined): string {
    if (!!!number) number = 0;
    var numEur = number / 100;
    return this.cp.transform(numEur, 'EUR', 'symbol', '1.0-0') || "";
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
    
  }
  onDocumentClick(event: MouseEvent) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }
  
  increment(index: number) {
    const extras = this.experiencePriceExtras;
    if (index >= 0 && index < extras.length) {
      const extra = extras[index];
      if (extra.currentQuantity < (extra.maxQuantity || Infinity)) {
        extra.currentQuantity++;
        // Crea un nuovo array per triggerare la change detection di Angular
        const newCounts = [...this.extraLabelCounts];
        newCounts[index] = extra.currentQuantity;
        this.extraLabelCounts = newCounts;
        this.calculateTotalPrice();
        this.updateValidation();
      }
    }
  }

  decrement(index: number) {
    const extras = this.experiencePriceExtras;
    if (index >= 0 && index < extras.length) {
      const extra = extras[index];
      if (extra.currentQuantity > (extra.minQuantity || 0)) {
        extra.currentQuantity--;
        // Crea un nuovo array per triggerare la change detection di Angular
        const newCounts = [...this.extraLabelCounts];
        newCounts[index] = extra.currentQuantity;
        this.extraLabelCounts = newCounts;
        this.calculateTotalPrice();
        this.updateValidation();
      }
    }
  }
  
  updateValidation() {
    // Usa la nuova struttura con details
    const minParties = this.experience?.details?.minPartiesToEnable ?? 0;
    const maxParties = this.experience?.details?.maxParties ?? Infinity;
  
    let totalPeople = this.numberOfPeople + this.extraLabelCounts.reduce((acc, count) => acc + count, 0);

    // Non modifichiamo più extraLabelCounts qui - causa conflitti con increment/decrement
    // Questa funzione si occupa solo di validazione, non di modificare i contatori
    
    this.isValid = totalPeople >= minParties && totalPeople <= maxParties;
  
    this.updateDropdownLabel();
}
  

updateDropdownLabel() {
  const extras = this.experiencePriceExtras;
  if (extras && extras.length > 0) {
    let additionalCounts = this.extraLabelCounts.map((count, index) => {
      const extra = extras[index];
      return `${count} ${extra.name?.toLowerCase() || 'extra'}`;
    }).join(' , ');
    
    if (additionalCounts.length > this.maxLabelLength!) {
      additionalCounts = additionalCounts.substring(0, this.maxLabelLength! - 3) + '...';
    }
   
    this.dropdownLabel = `<strong>${additionalCounts}</strong>`;
  } else {
    this.dropdownLabel = 'Extra';
  }
}


calculateTotalPrice() {
  let extraPrice = 0;
  const extras = this.experiencePriceExtras;
  
  for (let i = 0; i < this.extraLabelCounts.length && i < extras.length; i++) {
    const count = this.extraLabelCounts[i];
    const extra = extras[i];
    extraPrice += count * (extra.price_cents || 0);
  }

  this.totalPrice = extraPrice;
  this.totalPriceChange.emit(this.totalPrice); // Emetti il prezzo totale aggiornato
}
reset() {
  const extras = this.experiencePriceExtras;
  if (extras && extras.length > 0) {
    // Reimposta le quantità degli extra a zero
    extras.forEach((extra: any) => extra.currentQuantity = 0);
  
    // Aggiorna i contatori delle etichette
    this.extraLabelCounts = extras.map((extra: any) => extra.currentQuantity);
    
    // Calcola di nuovo il prezzo totale
    this.calculateTotalPrice();

    // Resetta lo stato di validità
    this.isValid = false;

    // Aggiorna l'etichetta del dropdown a "Extra"
    this.dropdownLabel = 'Extra';
  } else {
    // Se non ci sono extra, resetta comunque i contatori
    this.extraLabelCounts = [];
    this.totalPrice = 0;
    this.isValid = false;
    this.dropdownLabel = 'Extra';
  }
}
getExtraData() {
  return {
    labelCounts: this.extraLabelCounts,
    totalPrice: this.totalPrice
  };
}

}

